export const LIVE_SUBSCRIPTION = new Set(["active", "past_due", "suspended"]);

const FEATURE_RULES = [
  { key: "integration.jahez.branches", test: /jahez/i },
  { key: "integration.keeta.branches", test: /keeta/i },
  { key: "integration.ninja.branches", test: /ninja/i },
  { key: "integration.thechefz.branches", test: /chefz/i },
  { key: "pos.foodics.branches", test: /foodics/i },
  { key: "pos.tabsense.branches", test: /tabsense|tab sense/i },
  { key: "addon.trackhub.branches", test: /trackhub|track hub/i },
  { key: "addon.orderagent.branches", test: /order\s*agent/i },
  { key: "addon.digitalcatalog.branches", test: /digital\s*catalog/i },
];

const FEATURE_BY_TYPE = {
  ORDER_AGENT: "addon.orderagent.branches",
  CHAT_AGENT: "addon.orderagent.branches",
  DIGITAL_CATALOG: "addon.digitalcatalog.branches",
  TRACK_HUB: "addon.trackhub.branches",
};

const POS_TEST = /foodics|tabsense|tab sense/i;

export function appId(app) {
  return String(app?._id || app?.id || "");
}

export function featureKeyForApp(app) {
  const text = `${app?.name || ""} ${app?.name_ar || ""} ${app?.appType || ""}`;
  const named = FEATURE_RULES.find((rule) => rule.test.test(text));
  if (named) return named.key;
  return FEATURE_BY_TYPE[String(app?.appType || "").toUpperCase()] || null;
}

export function entitlementQtyForApp(app, subscription) {
  const key = featureKeyForApp(app);
  if (!key) return 0;
  const raw = subscription?.entitlements?.[key];
  if (raw == null || raw === "") return 0;
  const qty = Number(raw);
  return Number.isFinite(qty) && qty > 0 ? qty : 0;
}

export function sectionOf(app) {
  if (app?.category === "ADDON" || String(featureKeyForApp(app) || "").startsWith("addon.")) return "addon";
  if (POS_TEST.test(`${app?.name || ""} ${app?.appType || ""}`) || String(featureKeyForApp(app) || "").startsWith("pos.")) {
    return "pos";
  }
  return "integration";
}

function appMatchRule(app) {
  const text = `${app?.name || ""} ${app?.name_ar || ""} ${app?.appType || ""}`;
  return FEATURE_RULES.find((rule) => rule.test.test(text)) || null;
}

export function mergeBundleItems(items) {
  const byCode = new Map();
  (items || []).forEach((entry) => {
    const planCode = entry?.planCode || entry?.plan_code;
    const quantity = Number(entry?.quantity || 0);
    if (!planCode || quantity <= 0) return;
    const prev = byCode.get(planCode);
    const mergedQty = prev ? Math.max(prev.quantity, quantity) : quantity;
    byCode.set(planCode, { planCode, quantity: mergedQty });
  });
  return [...byCode.values()];
}

export function planForApp(app, plans) {
  const key = featureKeyForApp(app);
  const rule = appMatchRule(app);
  const matches = (plans || []).filter((plan) => {
    if (plan.global) return false;
    if (key && plan.featureKey === key) return true;
    if (!rule) return false;
    return rule.test.test(`${plan.name || ""} ${plan.code || ""} ${plan.featureKey || ""}`);
  });
  matches.sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0));
  return matches[0] || null;
}

export function subscriptionItemForApp(app, subscription, plans) {
  const key = featureKeyForApp(app);
  const rule = appMatchRule(app);
  return (subscription?.items || []).find((row) => {
    if (key && row.featureKey === key) return true;
    const text = `${row.planName || ""} ${row.planCode || ""}`;
    if (rule && rule.test.test(text)) return true;
    const plan = (plans || []).find((entry) => entry.code && entry.code === row.planCode);
    if (!plan || plan.global) return false;
    if (key && plan.featureKey === key) return true;
    return Boolean(rule && rule.test.test(`${plan.name || ""} ${plan.code || ""} ${plan.featureKey || ""}`));
  }) || null;
}

export function isLaunchPlan(plan) {
  if (!plan) return false;
  if (plan.launch === true) return true;
  const category = String(plan.category || "").toLowerCase();
  if (category === "launch") return true;
  const code = String(plan.code || "");
  if (/_launch$/i.test(code) || /\.launch$/i.test(code)) return true;
  return /\blaunch\b/i.test(`${plan.name || ""} ${code}`);
}

function launchBranchLimit(subscription) {
  const values = Object.entries(subscription?.entitlements || {})
    .filter(([key]) => String(key).endsWith(".branches"))
    .map(([, value]) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);
  return values.length ? Math.min(...values) : 20;
}

export function heldLaunchPlan(subscription, plans) {
  if (subscription?.launch?.code) {
    const fromCatalog = (plans || []).find((plan) => plan.code === subscription.launch.code);
    const branchLimit = Number(subscription.launch.branchLimit)
      || (fromCatalog ? branchLimitFromPlanCatalog(fromCatalog) : null)
      || launchBranchLimit(subscription);
    return {
      ...(fromCatalog || {}),
      code: subscription.launch.code,
      name: subscription.launch.name || fromCatalog?.name || "Launch",
      launch: true,
      branchLimit,
    };
  }
  const items = subscription?.items || [];
  const codes = new Set(items.map((item) => item.planCode));
  const fromCatalog = (plans || []).find((plan) => codes.has(plan.code) && isLaunchPlan(plan));
  if (fromCatalog) return fromCatalog;
  const item = items.find((row) => isLaunchPlan({ code: row.planCode, name: row.planName }));
  if (!item) {
    const handoffPending = Boolean(
      subscription?.scheduledChange?.effectiveAt
      && (subscription.scheduledChange.items || []).some(
        (row) => !isLaunchPlan({ code: row.planCode, name: row.planName }),
      ),
    );
    const inTrial = Boolean(
      subscription?.trialEndsAt
      && new Date(subscription.trialEndsAt).getTime() > Date.now(),
    );
    if (handoffPending || inTrial) {
      const launchPlan = (plans || []).find((plan) => isLaunchPlan(plan));
      if (launchPlan) {
        return {
          ...launchPlan,
          branchLimit: launchBranchLimit(subscription),
        };
      }
    }
    return null;
  }
  return {
    code: item.planCode,
    name: item.planName || "Launch",
    launch: true,
    branchLimit: launchBranchLimit(subscription),
  };
}

function branchLimitFromPlanCatalog(plan) {
  const fromPlan = Number(plan?.branchLimit);
  if (Number.isFinite(fromPlan) && fromPlan > 0) return fromPlan;
  const includes = plan?.includes || [];
  const branchRow = includes.find((row) => String(row.featureKey || row.key || "").endsWith(".branches"));
  const fromInclude = Number(branchRow?.limit ?? branchRow?.quantity ?? branchRow?.value);
  return Number.isFinite(fromInclude) && fromInclude > 0 ? fromInclude : null;
}

export function globalBranchLimitFromEntitlements(subscription) {
  const values = Object.entries(subscription?.entitlements || {})
    .filter(([key]) => String(key).endsWith(".branches"))
    .map(([, value]) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);
  return values.length ? Math.min(...values) : null;
}

/** Shared branch cap for Global / Launch (plan catalog + entitlements). */
export function resolvedCoveringBranchLimit(subscription, plan) {
  if (!plan) return 0;
  return (
    branchLimitFromPlanCatalog(plan)
    || globalBranchLimitFromEntitlements(subscription)
    || Number(plan.branchLimit || 0)
    || 0
  );
}

export function branchLimitForConnect(app, subscription, plans) {
  const covering = heldCoveringPlan(subscription, plans);
  if (covering && (isGlobalCatalogPlan(covering) || covering.launch)) {
    const shared = resolvedCoveringBranchLimit(subscription, covering);
    if (shared > 0) return shared;
  }
  const entitled = entitlementQtyForApp(app, subscription);
  if (entitled > 0) return entitled;
  const item = subscriptionItemForApp(app, subscription, plans);
  return Number(item?.quantity || 0);
}

export function heldCoveringPlan(subscription, plans) {
  return heldLaunchPlan(subscription, plans) || heldGlobalPlan(subscription, plans);
}

export function isGlobalCatalogPlan(plan) {
  if (!plan || isLaunchPlan(plan)) return false;
  if (plan.global === true) return true;
  const category = String(plan.category || "").toLowerCase();
  if (category === "global") return true;
  return /\bglobal\b/i.test(`${plan.name || ""} ${plan.code || ""}`);
}

export function globalPlans(plans) {
  return (plans || [])
    .filter((plan) => isGlobalCatalogPlan(plan))
    .sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0));
}

export function heldGlobalPlan(subscription, plans) {
  if (subscription?.global?.code) {
    const fromCatalog = (plans || []).find((plan) => plan.code === subscription.global.code);
    const branchLimit = Number(subscription.global.branchLimit)
      || (fromCatalog ? resolvedCoveringBranchLimit(subscription, fromCatalog) : null)
      || branchLimitFromPlanCatalog(fromCatalog);
    return {
      ...(fromCatalog || {}),
      code: subscription.global.code,
      name: subscription.global.name || fromCatalog?.name || "Global",
      global: true,
      branchLimit: branchLimit || fromCatalog?.branchLimit,
    };
  }
  const globals = globalPlans(plans);
  const codes = new Set((subscription?.items || []).map((item) => item.planCode));
  let match = globals.find((plan) => codes.has(plan.code));
  if (!match) {
    for (const item of subscription?.items || []) {
      const text = `${item.planName || ""} ${item.planCode || ""}`;
      if (!/\bglobal\b/i.test(text)) continue;
      match = globals.find((plan) => plan.code === item.planCode)
        || globals.find((plan) => /\bglobal\b/i.test(`${plan.name || ""} ${plan.code || ""}`));
      if (match) break;
    }
  }
  if (!match) return null;
  const branchLimit = resolvedCoveringBranchLimit(subscription, match);
  return branchLimit > 0 ? { ...match, branchLimit } : match;
}

export function connectionsForApp(app, rows) {
  const id = appId(app);
  const key = featureKeyForApp(app);
  return (rows || []).filter((row) => {
    if (id && row.appId) return String(row.appId) === id;
    return Boolean(key && row.featureKey === key);
  });
}

export function connectedBranchCount(app, rows) {
  const ids = new Set();
  connectionsForApp(app, rows).forEach((row) => {
    const list = Array.isArray(row.branchIds) && row.branchIds.length ? row.branchIds : [row.branchId];
    list.filter(Boolean).forEach((branchId) => ids.add(String(branchId)));
  });
  return ids.size;
}

export function connectPath(app) {
  const id = appId(app);
  if (!id) return "/apps-management";
  if (app.appType === "TRACK_HUB") return `/trackhub-integration/${id}`;
  if (app.category === "ADDON") return `/addon-integration/${id}`;
  return `/add-app/${id}`;
}

export function formatMinor(value, language) {
  const amount = Number(value || 0) / 100;
  return amount.toLocaleString(language || undefined, {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

export function formatDate(value, locale) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const language = String(locale || "").toLowerCase().startsWith("ar") ? "ar" : "en-GB";
  return date.toLocaleDateString(language, { day: "numeric", month: "short" });
}

/** Calendar day after a period-end instant (next billing / plan start day). */
export function addCalendarDay(value) {
  if (value == null || value === "") return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + 1);
  return next;
}

/**
 * Label for tier footnotes: next day after current period end when subscribed;
 * today when there is no active period end.
 */
export function formatPeriodStartLabel({
  subscription,
  renewsAt,
  managing,
  useTrialEnd,
  locale,
}) {
  if (!managing) {
    return formatDate(new Date(), locale);
  }
  const endRaw = useTrialEnd
    ? (subscription?.trialEndsAt || renewsAt || subscription?.renewsAt || subscription?.currentPeriodEnd)
    : (renewsAt || subscription?.renewsAt || subscription?.currentPeriodEnd);
  if (!endRaw) {
    return formatDate(new Date(), locale);
  }
  const nextDay = addCalendarDay(endRaw);
  return formatDate(nextDay, locale);
}
